import type {
  Duration,
  SchedulerDistributionRuleType,
  ConferenceIntegrations,
} from 'src/types/scheduler';
import type {LocationMeta} from 'src/types/scheduler/templates';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';


type TFunction = (key: string, value: string) => string;

import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import isTomorrow from 'date-fns/isTomorrow';
import momentTZ from 'moment-timezone';


export const getTemplateEnum = (
  t: (key: string, value: string) => string,
): {
  [key: string]: string;
} => {
  return {
    single_account: `1:1 ${t('Individual', 'Individual')}`,
    round_robin_availability: `${t('Round_Robin', 'Round Robin')}`,
    round_robin_fairness: `${t('Round_Robin', 'Round Robin')}`,
    one_slot_multiple_event: `${t('MultiEvent', 'Multi-Event')}`,
    panel_interview: t('Panel', 'Panel'),
    one_event_multiple_attendee: `${t('MultiAttendee', 'Multi-Attendee')}`,
    sequential_interview: `${t('Sequential', 'Sequential')}`,
  };
};

export const getDistributionRuleDisplayName = (
  distributionRule: SchedulerDistributionRuleType,
  t: TFunction,
): string => {
  return (
    {
      single_account: t('11', '1:1'),
      round_robin_availability: `${t('Round_Robin', 'Round Robin')}`,
      round_robin_fairness: `${t('Round_Robin', 'Round Robin')}`,
      one_slot_multiple_event: `${t('MultiEvent', 'Multi-Event')}`,
      panel_interview: t('Panel', 'Panel'),
      one_event_multiple_attendee: `${t('MultiAttendee', 'Multi-Attendee')}`,
      sequential_interview: `${t('Sequential', 'Sequential')}`,
    }[distributionRule] || ''
  );
};

export const getDurationLabel = (
  duration: Duration,
  t: TFunction,
  showDurationInCompactFormat?: boolean,
): string => {
  const isPlural = duration.amount !== 1;

  const units = {
    minutes: isPlural
      ? showDurationInCompactFormat
        ? t('mins', 'mins')
        : t('Minutes', 'Minutes')
      : showDurationInCompactFormat
      ? t('min', 'min')
      : t('Minute', 'Minute'),
    hours: isPlural
      ? showDurationInCompactFormat
        ? t('hrs', 'hrs')
        : t('Hours', 'Hours')
      : showDurationInCompactFormat
      ? t('hr', 'hr')
      : t('Hour', 'Hour'),
    days: isPlural
      ? showDurationInCompactFormat
        ? t('days', 'days')
        : t('Days', 'Days')
      : showDurationInCompactFormat
      ? t('day', 'day')
      : t('Day', 'Day'),
    weeks: isPlural
      ? showDurationInCompactFormat
        ? t('wks', 'wks')
        : t('Weeks', 'Weeks')
      : showDurationInCompactFormat
      ? t('wk', 'wk')
      : t('Week', 'Week'),
  } as const;

  return `${duration.amount} ${units[duration.unit]}`;
};

export function formatDate(
  value: string,
  t: (key: string, value: string) => string,
  makeKey: (key: string) => string,
): string {
  const date = new Date(value);
  const day = format(date, 'EEEE');
  const month = format(date, 'MMMM');
  const dateNumber = date.getDate();
  const year = date.getFullYear();

  const dateString = `${t(makeKey(month), month)} ${dateNumber}, ${year}`;

  const commonDateString = `${t(makeKey(day), day)}, ${t(
    makeKey(month),
    month,
  )} ${dateNumber}, ${year}`;

  if (isToday(date)) {
    return `${t('Today', 'Today')} • ${dateString}`;
  } else if (isTomorrow(date)) {
    return `${t('Tomorrow', 'Tomorrow')}  • ${dateString}`;
  } else {
    return commonDateString;
  }
}

export const getMeetingLinkOptions = (
  conferenceIntegrations: ConferenceIntegrations,
  t: (key: string, value: string) => string,
): MenuOption[] => {
  const options: MenuOption[] = [
    {
      label: `${t('Custom_Location', 'Custom Location')}`,
      key: 'custom_location',
    },
  ];

  if (conferenceIntegrations.microsoft_teams) {
    options.push({
      label: `${t('Microsoft_Teams', 'Microsoft Teams')}`,
      key: 'microsoft_teams',
    });
  }

  if (conferenceIntegrations.google_meet) {
    options.push({
      label: `${t('Google_Meet', 'Google Meet')}`,
      key: 'google_meet',
    });
  }

  if (conferenceIntegrations.zoom) {
    options.push({
      label: `${t('Zoom', 'Zoom')}`,
      key: 'zoom',
    });
  }

  if (conferenceIntegrations.webex) {
    options.push({
      label: `${t('Webex', 'Webex')}`,
      key: 'webex',
    });
  }

  return options;
};

/**
 * Calculates the total duration of rounds in a day (including breaks)
 * and formats it as "Duration: 2hr 15 mins | 2 Rounds"
 */
export const calculateDayDurationSummary = (
  dayRounds: Array<{
    type: 'round' | 'break';
    value: {template_id?: string; amount?: number; unit?: string};
  }>,
  sequentialRounds:
    | Record<string, {duration: {amount: number; unit: 'minutes' | 'hours'}}>
    | null
    | undefined,
  t: TFunction,
): string => {
  // Filter only rounds (to count them separately)
  const rounds = dayRounds.filter((item) => item.type === 'round');

  if (rounds.length === 0 || !sequentialRounds) {
    return '';
  }

  // Calculate total duration in minutes (including both rounds and breaks)
  const totalMinutes = dayRounds.reduce((acc, item) => {
    if (item.type === 'round' && item.value.template_id) {
      const template = sequentialRounds[item.value.template_id];
      if (template) {
        const durationAmount = template.duration.amount;
        const durationUnit = template.duration.unit;

        // Convert to minutes
        return (
          acc +
          (durationUnit === 'hours' ? durationAmount * 60 : durationAmount)
        );
      }
    } else if (item.type === 'break' && item.value.amount && item.value.unit) {
      // Add break duration
      const breakAmount = item.value.amount;
      const breakUnit = item.value.unit;

      // Convert to minutes
      return acc + (breakUnit === 'hours' ? breakAmount * 60 : breakAmount);
    }

    return acc;
  }, 0);

  // Convert back to hours and minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Format the duration string
  const durationParts: string[] = [];

  if (hours > 0) {
    durationParts.push(`${hours}${t('hr', 'hr')}`);
  }

  if (minutes > 0) {
    durationParts.push(`${minutes} ${t('mins', 'mins')}`);
  }

  const durationStr = durationParts.join(' ');
  const roundCount = rounds.length;
  const roundsLabel =
    roundCount === 1 ? t('Round', 'Round') : t('Rounds', 'Rounds');

  return `${t(
    'Duration',
    'Duration',
  )}: ${durationStr} | ${roundCount} ${roundsLabel}`;
};

// Location meta helpers (shared by schedule-now-form, location-details)
export const getMeetingRoomEntry = (meta: LocationMeta[] | undefined) =>
  (meta ?? []).find((location) => location.type === 'meeting_room');

export const getBaseLocationMeta = (meta: LocationMeta[] | undefined) =>
  (meta ?? []).filter((location) => location.type !== 'meeting_room');

//Keeps primary location / links; replaces any existing meeting-room slice with the new choice (or drops it when `roomKey === noneKey`)
export const locationMetaWithMeetingRoom = (
  meta: LocationMeta[] | undefined,
  roomKey: string,
  noneKey: string,
): LocationMeta[] => {
  const withoutMeetingRoom = getBaseLocationMeta(meta);
  return roomKey === noneKey
    ? withoutMeetingRoom
    : [
        ...withoutMeetingRoom,
        {type: 'meeting_room' as const, meta: {rooms: [roomKey]}},
      ];
};

export const getPrimaryLocationKey = (
  meta: LocationMeta[] | undefined,
): string | undefined => {
  const primary = (meta ?? []).find(
    (location) =>
      location.type === 'custom_location' || location.type === 'meeting_link',
  );
  return primary
    ? primary.type === 'custom_location'
      ? 'custom_location'
      : primary.meta?.provider
    : undefined;
};

// Shared label format for room dropdowns (Schedule Now, template editor)
export const formatRoomOptionLabel = (
  room: {name: string; capacity?: number},
  t: (key: string, value: string) => string,
): string =>
  `${room.name} - ${room.capacity ?? 0} ${t('occupants', 'occupants')}`;

// Rooms availability params (schedule-now meeting-room dropdown).
export const buildRoomsAvailabilityParams = (
  timeSlot: {date?: string; start_time?: string} | undefined,
  durationMinutes: number,
  viewerTimeZone: string | undefined,
): {startDt: string; endDt: string} | null => {
  if (!timeSlot?.date || !timeSlot.start_time || !viewerTimeZone) {
    return null;
  }
  const start = momentTZ.tz(
    `${timeSlot.date}T${timeSlot.start_time.trim()}`,
    viewerTimeZone,
  );
  if (!start.isValid()) {
    return null;
  }
  const end = start.clone().add(durationMinutes, 'minutes');
  return {
    startDt: start.format('YYYY-MM-DDTHH:mm:ss'),
    endDt: end.format('YYYY-MM-DDTHH:mm:ss'),
  };
};
